import { createFileRoute } from '@tanstack/react-router'
import { useState, useMemo } from 'react'

import type { InventoryLocationDetail } from '@/api/admin-inventory-types'
import { useProvisionInventoryLocationLevels, useUpdateInventoryLocation } from '@/api/api-mutations'
import { useListSalesChannels, preloadListSalesChannels } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { Text } from '@/components/base/text'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'

export const Route = createFileRoute('/_authenticated/settings/inventory/locations/$inventoryLocationUuid/edit')({
	component: RouteComponent,
	loader: async ({ context }) => {
		await preloadListSalesChannels(context, {})
	},
	staticData: {
		crumb: ({ context: { L } }) => L.Inventory.Settings.Locations.EditTitle()
	}
})

function RouteComponent() {
	const { inventoryLocationUuid } = Route.useParams()
	return (
		<RouteModal
			asDrawer
			requiresData
			loader={async api => await api.getInventoryLocation({ path: { inventoryLocationUuid } })}
		>
			{data => <EditInventoryLocationForm location={data} />}
		</RouteModal>
	)
}

function EditInventoryLocationForm({ location }: { location: InventoryLocationDetail }) {
	const { L } = useI18n()
	const { handleSuccess } = useRouteModal()
	const { inventoryLocationUuid } = Route.useParams()
	const updateLocation = useUpdateInventoryLocation()
	const provisionLevels = useProvisionInventoryLocationLevels(true)
	const salesChannels = useListSalesChannels()
	const salesChannelOptions = useMemo(
		() =>
			(salesChannels.data ?? []).map(channel => ({
				value: channel.uuid,
				label:
					channel.inventoryLocation && channel.inventoryLocation.uuid !== inventoryLocationUuid
						? `${channel.name} (${channel.inventoryLocation.name})`
						: channel.name
			})),
		[salesChannels.data, inventoryLocationUuid]
	)
	const [initializing, setInitializing] = useState(false)
	const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
	const [initError, setInitError] = useState(false)

	const form = useAppForm({
		defaultValues: {
			name: location.name,
			salesChannelUuids: location.salesChannels.map(channel => channel.uuid)
		},
		onSubmit: async ({ value }) => {
			const result = await updateLocation({
				path: { inventoryLocationUuid },
				body: {
					name: value.name,
					salesChannelUuids: value.salesChannelUuids
				}
			})
			if (result.isSuccess) {
				toast.success(L.Inventory.Settings.Locations.EditSuccess())
				handleSuccess()
			} else {
				toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
			}
		}
	})

	const initializeStock = async () => {
		setInitializing(true)
		setInitError(false)
		let done = 0
		let total = 0
		try {
			let complete = false
			while (!complete) {
				const result = await provisionLevels.mutateAsync({
					path: { inventoryLocationUuid },
					body: {}
				})
				if (!result.isSuccess) {
					setInitError(true)
					toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
					break
				}
				done += result.response.created
				total = Math.max(total, done + result.response.remaining)
				setProgress({ done, total })
				complete = result.response.complete
			}
			if (complete) {
				toast.success(L.Inventory.Settings.Locations.InitializeStockSuccess())
			}
		} catch (error) {
			setInitError(true)
			toast.error(error instanceof Error ? error.message : L.Error.SomethingWentWrong())
		} finally {
			setInitializing(false)
		}
	}

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.Inventory.Settings.Locations.EditTitle()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						<form.AppField
							name='name'
							validators={{ onChange: ({ value }) => (!value ? L.General.Required() : undefined) }}
							children={field => <field.TextField autoFocus label={L.Fields.Name()} />}
						/>
					</FormFieldGroup>
					<FormFieldGroup>
						<form.AppField
							name='salesChannelUuids'
							children={field => (
								<field.ComboboxField
									label={L.Inventory.Settings.Locations.LinkedChannels()}
									optional
									multiple
									options={salesChannelOptions}
									optionsLoading={salesChannels.isLoading}
								/>
							)}
						/>
						<Text className='text-sm text-ui-fg-subtle'>
							{L.Inventory.Settings.Locations.LinkedChannelsHint()}
						</Text>
					</FormFieldGroup>
					<FormFieldGroup>
						<Text className='font-medium text-ui-fg-base'>
							{L.Inventory.Settings.Locations.InitializeStock()}
						</Text>
						<Text className='text-sm text-ui-fg-subtle'>
							{L.Inventory.Settings.Locations.InitializeStockDescription()}
						</Text>
						{progress && progress.total > 0 && (
							<div className='mt-2'>
								<Text className='mb-1 text-sm text-ui-fg-subtle'>
									{L.Inventory.Settings.Locations.InitializeStockProgressLabel({
										created: progress.done,
										total: progress.total
									})}
								</Text>
								<div className='h-2 w-full overflow-hidden rounded-full bg-ui-bg-component'>
									<div
										className='h-full bg-ui-bg-interactive transition-all'
										style={{
											width: `${Math.min(100, Math.round((progress.done / progress.total) * 100))}%`
										}}
									/>
								</div>
							</div>
						)}
						{initError && (
							<Text className='text-sm text-ui-fg-error'>
								{L.Inventory.Settings.Locations.InitializeStockError({ created: progress?.done ?? 0 })}
							</Text>
						)}
						<Button
							type='button'
							variant='secondary'
							size='small'
							className='mt-2 self-start'
							isLoading={initializing}
							disabled={initializing || provisionLevels.isPending}
							onClick={() => void initializeStock()}
						>
							{initError
								? L.Inventory.Settings.Locations.InitializeStockRetry()
								: L.Inventory.Settings.Locations.InitializeStock()}
						</Button>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<form.SaveButton />
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}
